import React, { type ReactElement } from 'react';
import clsx from 'clsx';
import { Controller, useController } from 'react-hook-form';
import type { FieldPath, FieldValues } from 'react-hook-form';
import MultiComboBoxFormLibraryAgnostic from 'atom-components/combo-boxes/multi-combo-box';
import type {
  ComboBoxItem as ImportedComboBoxItem,
  ComboBoxItemId as ImportedComboBoxItemId
} from 'atom-components/combo-boxes/types';
import { type SharedComboBoxProps } from './types';

/**
 * The ComboBoxItem "id" field must be the same as the React Hook Form field array item that is
 * being controlled. The ComboBoxItem "name" field can be any arbitrary value and represents a
 * friendly version of the field that will be displayed in the ComboBox.
 * Note: This type is imported from the combo box atom components that can be used outside of React
 * Hook Form.
 */
export type ComboBoxItem = ImportedComboBoxItem;
export type ComboBoxItemId = ImportedComboBoxItemId;

type MultiComboBoxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = SharedComboBoxProps<TFieldValues, TName> & {
  onChange?: (items: ComboBoxItem[]) => void;
};

/**
 * A controlled multiple selection combo box, intended to make using our Downshift combo box less
 * verbose and more consistent across different forms that are using React Hook Form.
 */
export default function MultiComboBox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  control,
  name,
  allItems,
  className,
  clearable,
  disabled,
  loading,
  onChange
}: MultiComboBoxProps<TFieldValues, TName>): ReactElement {
  const { field, formState } = useController({ control, name });

  // The form's defaultValues must already be set at this point since the combo box will only load
  // the initial selected items once and won't repopulate them on re-render:
  const selectedItems = allItems.filter((item) => field.value.includes(item.id));

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={(): ReactElement => (
        <MultiComboBoxFormLibraryAgnostic
          id={id}
          allItems={allItems}
          initialSelectedItems={selectedItems}
          externallySelectedItems={selectedItems}
          reinitialisable
          className={clsx(className)}
          hasError={formState.errors[field.name] !== undefined}
          clearable={clearable}
          disabled={disabled}
          loading={loading}
          onChange={onChange} // Todo: can we implement this in a more controlled way?
          clearCondition={field.value === undefined}
        />
      )}
    />
  );
}
